import React, { useMemo } from 'react';
import { Dispatch } from 'redux';
import { Alert, Button, Input, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
    EyeInvisibleOutlined,
    EyeOutlined,
    InfoCircleOutlined,
    LockOutlined,
    MailOutlined,
} from '@ant-design/icons';
import { changeDefaultPassword, login } from 'redux/actions/user-actions';
import { passwordChangeRequired, userState } from 'redux/selectors/user-selectors';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { IUserState } from 'redux/reducers/user/user-reducer';

interface IProps {
    login: Function;
    passwordChangeRequired: boolean;
    changePassword: Function;
    isLoading: boolean;
}

interface InitialValues {
    email: string;
    password: string;
    newPassword: string;
    confirmPassword: string;
}

interface LoginInputProps {
    name: keyof InitialValues;
    errors: Record<string, string>;
    touched: Record<string, boolean>;
}

const inputClassName =
    "h-14 rounded-[20px] border-slate-200 bg-white/95 px-4 text-[15px] font-semibold tracking-[0.01em] text-slate-900 shadow-none transition-all duration-200 [font-family:futura-pt,sans-serif] placeholder:font-medium placeholder:text-slate-400 hover:border-red-200 focus-within:border-red-300 focus-within:shadow-[0_0_0_4px_rgba(220,38,38,0.08)] [&>input]:bg-transparent [&>input]:[font-family:futura-pt,sans-serif] [&>input]:text-[15px] [&>input]:font-semibold [&>input]:tracking-[0.01em] [&>input]:text-slate-900 [&>input::placeholder]:font-medium [&>input::placeholder]:text-slate-400 [&>input:-webkit-autofill]:[-webkit-text-fill-color:#0f172a] [&>input:-webkit-autofill]:[-webkit-box-shadow:0_0_0_1000px_rgba(255,255,255,0.95)_inset] [&>input:-webkit-autofill]:[box-shadow:0_0_0_1000px_rgba(255,255,255,0.95)_inset] [&>input:-webkit-autofill]:[caret-color:#0f172a] [&>input:-webkit-autofill]:[font-family:futura-pt,sans-serif] [&>input:-webkit-autofill:hover]:[-webkit-text-fill-color:#0f172a] [&>input:-webkit-autofill:hover]:[-webkit-box-shadow:0_0_0_1000px_rgba(255,255,255,0.95)_inset] [&>input:-webkit-autofill:focus]:[-webkit-text-fill-color:#0f172a] [&>input:-webkit-autofill:focus]:[-webkit-box-shadow:0_0_0_1000px_rgba(255,255,255,0.95)_inset]";

const errorTextClassName = 'mt-1 pl-2 text-sm font-semibold text-red-600';

const LoginInputField = ({ name, errors, touched }: LoginInputProps) => {
    const { t } = useTranslation();
    const isEmail = name === 'email';
    const isPassword = name === 'password';

    return (
        <div className="space-y-1.5">
            <Field name={name}>
                {({ field }: any) =>
                    isEmail ? (
                        <Input
                            {...field}
                            className={inputClassName}
                            placeholder={t(`login_${name}`)}
                            prefix={<MailOutlined className="mr-2 text-lg text-red-600" />}
                            autoComplete="username"
                        />
                    ) : (
                        <Input.Password
                            {...field}
                            className={inputClassName}
                            placeholder={t(`login_${name}`)}
                            prefix={<LockOutlined className="mr-2 text-lg text-red-600" />}
                            autoComplete={isPassword ? 'current-password' : 'new-password'}
                            iconRender={(visible) =>
                                visible ? (
                                    <EyeInvisibleOutlined className="text-slate-400" />
                                ) : (
                                    <EyeOutlined className="text-slate-400" />
                                )
                            }
                        />
                    )
                }
            </Field>
            {errors[name] && touched[name] ? (
                <p id={`login_error_message_empty_${name}`} className={errorTextClassName}>
                    {errors[name]}
                </p>
            ) : null}
        </div>
    );
};

const initialValues: InitialValues = {
    email: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
};

const LoginForm = ({ passwordChangeRequired, login, changePassword, isLoading }: IProps) => {
    const { t } = useTranslation();
    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                email: Yup.string()
                    .email(t('invalid_email_address'))
                    .required(t('email_is_required')),
                password: !passwordChangeRequired
                    ? Yup.string().required(t('password_is_required'))
                    : Yup.string().notRequired(),
                newPassword: passwordChangeRequired
                    ? Yup.string()
                          .min(8, t('pass_8_chars'))
                          .matches(/[A-Z]/, t('pass_capital'))
                          .matches(/[!@#$%^&*(),.?":{}|<>]/, t('pass_special'))
                          .matches(/[0-9]/, t('pass_number'))
                          .matches(/^\S*$/, t('pass_spaces'))
                          .required(t('new_password_is_required'))
                    : Yup.string().notRequired(),
                confirmPassword: passwordChangeRequired
                    ? Yup.string()
                          .required(t('password_confirmation_is_required'))
                          .oneOf([Yup.ref('newPassword')], t('passwords_must_match'))
                    : Yup.string().notRequired(),
            }),
        [passwordChangeRequired, t],
    );

    const onSubmit = (
        { email, password, newPassword }: InitialValues,
        { setFieldValue }: any,
    ) => {
        if (passwordChangeRequired) {
            changePassword({ email, oldPassword: password, newPassword });
            setFieldValue('password', '');
            setFieldValue('newPassword', '');
            setFieldValue('confirmPassword', '');
        } else {
            login({ email, password });
        }
    };

    return (
        <div className="w-full max-w-[32rem]">
            <div className="rounded-[32px] border border-white/70 bg-white/90 p-6 shadow-[0_24px_80px_rgba(15,23,42,0.12)] backdrop-blur md:p-10">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                >
                    {({ errors, touched }) => (
                        <Form className="relative flex min-h-[15rem] flex-col justify-between md:min-h-[16.5rem]">
                            <div
                                className={`flex min-h-[15rem] flex-col justify-between transition-all duration-300 md:min-h-[16.5rem] ${
                                    isLoading
                                        ? 'pointer-events-none translate-y-2 opacity-0'
                                        : 'translate-y-0 opacity-100'
                                }`}
                            >
                                <div className="space-y-3">
                                    <h2 className="text-5xl mb-5 font-black tracking-[-0.04em] text-slate-950 [font-family:futura-pt,sans-serif] max-sm:text-[3rem]">
                                        {t('welcome_back')}
                                    </h2>
                                    {passwordChangeRequired ? (
                                        <Alert
                                            showIcon
                                            icon={<InfoCircleOutlined />}
                                            type="info"
                                            className="rounded-2xl border border-slate-200 bg-slate-50 text-slate-700"
                                            message={t('change_default_password')}
                                        />
                                    ) : null}
                                </div>

                                <div className="flex flex-1 flex-col justify-end">
                                    <div className="space-y-4">
                                        <LoginInputField
                                            errors={errors as Record<string, string>}
                                            touched={touched as Record<string, boolean>}
                                            name="email"
                                        />
                                        <LoginInputField
                                            errors={errors as Record<string, string>}
                                            touched={touched as Record<string, boolean>}
                                            name="password"
                                        />
                                        {passwordChangeRequired ? (
                                            <>
                                                <LoginInputField
                                                    errors={errors as Record<string, string>}
                                                    touched={touched as Record<string, boolean>}
                                                    name="newPassword"
                                                />
                                                <LoginInputField
                                                    errors={errors as Record<string, string>}
                                                    touched={touched as Record<string, boolean>}
                                                    name="confirmPassword"
                                                />
                                            </>
                                        ) : null}
                                    </div>

                                    <Button
                                        id="button_login"
                                        type="primary"
                                        htmlType="submit"
                                        size="large"
                                        block
                                        className="!mt-7 !h-14 !rounded-full !border-0 !bg-[linear-gradient(135deg,#cf0920,#df3654)] text-base font-bold tracking-[0.01em] shadow-[0_20px_40px_rgba(207,9,32,0.24)] hover:!bg-[linear-gradient(135deg,#cf0920,#df3654)] hover:shadow-[0_22px_46px_rgba(207,9,32,0.28)]"
                                    >
                                        {passwordChangeRequired
                                            ? t('login_update_password')
                                            : t('login_submit')}
                                    </Button>
                                </div>
                            </div>

                            <div
                                className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                                    isLoading
                                        ? 'pointer-events-auto opacity-100'
                                        : 'pointer-events-none opacity-0'
                                }`}
                            >
                                <div className="flex flex-col items-center gap-4">
                                    <Spin size="large" />
                                    <span className="text-sm font-semibold tracking-[0.01em] text-slate-500 [font-family:futura-pt,sans-serif]">
                                        Loading...
                                    </span>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    passwordChangeRequired: passwordChangeRequired(state),
    isLoading: (userState(state) as IUserState).isLoading,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    login: (params: any) => dispatch(login(params)),
    changePassword: (params: any) => dispatch(changeDefaultPassword(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
